import React, { useState } from 'react';
import { Button, Input, message, Typography } from 'antd';
import { X, Send, Share2, MessageCircle, HeartHandshake } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNpsSurvey } from '../hooks/useNpsSurvey';
import { useAnalytics } from '@/shared/providers/AnalyticsProvider';
import { useAuth } from '@/shared/contexts/AuthContext';

const { TextArea } = Input;
const { Text } = Typography;

type ScoreCategory = 'detractor' | 'passive' | 'promoter';

function getCategory(score: number): ScoreCategory {
  if (score <= 6) return 'detractor';
  if (score <= 8) return 'passive';
  return 'promoter';
}

interface FollowUpConfig {
  textKey: string;
  icon: React.ReactNode;
  color: string;
  showFeedbackForm: boolean;
  showReferralLink: boolean;
  showCsNotice: boolean;
}

const FOLLOW_UP_CONFIG: Record<ScoreCategory, FollowUpConfig> = {
  promoter: {
    textKey: 'nps.promoterText',
    icon: <Share2 size={16} />,
    color: '#10B981',
    showFeedbackForm: false,
    showReferralLink: true,
    showCsNotice: false,
  },
  passive: {
    textKey: 'nps.passiveText',
    icon: <HeartHandshake size={16} />,
    color: '#F59E0B',
    showFeedbackForm: true,
    showReferralLink: false,
    showCsNotice: false,
  },
  detractor: {
    textKey: 'nps.detractorText',
    icon: <MessageCircle size={16} />,
    color: '#EF4444',
    showFeedbackForm: true,
    showReferralLink: false,
    showCsNotice: true,
  },
};

const REFERRAL_LINK = `${window.location.origin}/solicitar-acesso?ref=nps`;

const NpsSurveyWidget: React.FC = () => {
  const { t } = useTranslation();
  const { visible, dismiss, submit } = useNpsSurvey();
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!visible) return null;

  const handleSubmit = async () => {
    if (selectedScore === null) return;
    setSubmitting(true);
    try {
      await submit(selectedScore, comment || undefined);
      trackEvent('nps_submitted', {
        score: selectedScore,
        feedback: comment || undefined,
        tenantId: user?.tenantId,
      });
      setSubmitted(true);
      message.success(t('nps.thankYouMessage'));
    } catch {
      message.error(t('nps.errorSubmitting'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyReferral = () => {
    navigator.clipboard.writeText(REFERRAL_LINK).then(() => {
      message.success(t('nps.linkCopied'));
    }).catch(() => {
      message.info(t('nps.referralLinkInfo') + REFERRAL_LINK);
    });
  };

  const category = selectedScore !== null ? getCategory(selectedScore) : null;
  const followUp = category ? FOLLOW_UP_CONFIG[category] : null;

  // Post-submission thank you screen
  if (submitted && category && followUp) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 380,
          background: '#FFFFFF',
          borderRadius: 12,
          boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
          zIndex: 1050,
          overflow: 'hidden',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        <div
          style={{
            background: followUp.color,
            padding: '16px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div style={{ color: '#fff', fontSize: 14, fontWeight: 500, lineHeight: 1.5, flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
            {followUp.icon}
            <span>{category === 'detractor' ? t('nps.thankYouFeedback') : t('nps.thankYou')}</span>
          </div>
          <button
            onClick={dismiss}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', padding: 4, marginLeft: 8, display: 'flex', alignItems: 'center' }}
            aria-label={t('nps.close')}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: '16px 20px' }}>
          <Text style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
            {t(followUp.textKey)}
          </Text>

          {followUp.showReferralLink && (
            <div style={{ marginTop: 12 }}>
              <Button
                type="primary"
                icon={<Share2 size={14} />}
                onClick={handleCopyReferral}
                block
                style={{ background: '#10B981', borderColor: '#10B981', borderRadius: 8 }}
              >
                {t('nps.copyReferralLink')}
              </Button>
            </div>
          )}

          {followUp.showCsNotice && (
            <div
              style={{
                marginTop: 12,
                padding: '8px 12px',
                background: '#FEF2F2',
                borderRadius: 8,
                fontSize: 12,
                color: '#991B1B',
              }}
            >
              {t('nps.csNotice')}
            </div>
          )}

          <Button
            type="text"
            onClick={dismiss}
            block
            style={{ marginTop: 12, color: 'var(--color-text-tertiary)' }}
          >
            {t('nps.close')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: 380,
        background: '#FFFFFF',
        borderRadius: 12,
        boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
        zIndex: 1050,
        overflow: 'hidden',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #6366F1, #818CF8)',
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ color: '#fff', fontSize: 14, fontWeight: 500, lineHeight: 1.5, flex: 1 }}>
          {t('nps.question')}
        </div>
        <button
          onClick={dismiss}
          style={{
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.8)',
            cursor: 'pointer',
            padding: 4,
            marginLeft: 8,
            display: 'flex',
            alignItems: 'center',
          }}
          aria-label={t('nps.close')}
        >
          <X size={16} />
        </button>
      </div>

      {/* Score buttons */}
      <div style={{ padding: '16px 20px' }}>
        <div
          style={{
            display: 'flex',
            gap: 4,
            justifyContent: 'space-between',
            marginBottom: 4,
          }}
        >
          {Array.from({ length: 11 }, (_, i) => {
            const isSelected = selectedScore === i;
            let bgColor = '#F1F5F9';
            let textColor = '#64748B';

            if (isSelected) {
              if (i <= 6) {
                bgColor = '#EF4444';
                textColor = '#FFFFFF';
              } else if (i <= 8) {
                bgColor = '#F59E0B';
                textColor = '#FFFFFF';
              } else {
                bgColor = '#10B981';
                textColor = '#FFFFFF';
              }
            }

            return (
              <button
                key={i}
                onClick={() => setSelectedScore(i)}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 6,
                  border: isSelected ? 'none' : '1px solid #E2E8F0',
                  background: bgColor,
                  color: textColor,
                  fontSize: 12,
                  fontWeight: isSelected ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {i}
              </button>
            );
          })}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 10,
            color: 'var(--color-text-tertiary)',
            padding: '0 2px',
          }}
        >
          <span>{t('nps.unlikely')}</span>
          <span>{t('nps.veryLikely')}</span>
        </div>
      </div>

      {/* Follow-up section */}
      {category && followUp && (
        <div style={{ padding: '0 20px 16px' }}>
          <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>
            {t(followUp.textKey)}
          </div>
          {followUp.showFeedbackForm && (
            <TextArea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('nps.feedbackPlaceholder')}
              rows={3}
              style={{ borderRadius: 8, fontSize: 13, resize: 'none' }}
            />
          )}
          {followUp.showCsNotice && (
            <div
              style={{
                marginTop: 8,
                padding: '6px 10px',
                background: '#FEF2F2',
                borderRadius: 6,
                fontSize: 11,
                color: '#991B1B',
              }}
            >
              {t('nps.csNoticeShort')}
            </div>
          )}
          <Button
            type="primary"
            icon={<Send size={14} />}
            onClick={handleSubmit}
            loading={submitting}
            block
            style={{
              marginTop: 12,
              height: 38,
              borderRadius: 8,
              background: '#6366F1',
              borderColor: '#6366F1',
            }}
          >
            {t('nps.submitFeedback')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default NpsSurveyWidget;
